import { FC, ReactElement, Children, useEffect, useCallback, useMemo } from "react";
import { View } from "react-native";

import { tailwind } from "src/foundations/styles";
import AccordionDetails from "src/ui/components/Accordion/AccordionDetails";
import AccordionSummary from "src/ui/components/Accordion/AccordionSummary";
import Collapsible from "src/ui/components/Collapsible";
import useControlled from "src/ui/hooks/useControlled";

import AccordionContext, { AccordionContextState } from "./AccordionContext";
import { ANIMATION_OPEN_CLOSE_DURATION } from "./constants";

type AccordionAcceptedChildrenTypes = typeof AccordionSummary | typeof AccordionDetails;

export type AccordionProps = {
  /**
   * determines whether accordion is disable or not
   */
  // TODO: handle disabled look
  disabled?: boolean;
  /**
   * gives users the ability to hide the bottom border
   */
  hideBorder?: boolean;
  /**
   * determines if accordion is expanded or not. if set component
   * is understood to be a controlled component
   */
  expanded?: boolean;
  /**
   * determine whether or not the accordion is initially expanded
   * if set component is seen as being a uncontrolled component
   */
  defaultExpanded?: boolean;
  /**
   * children of accordion which should only be AccordionSummary and AccordionDetails
   */
  children?: ReactElement<AccordionAcceptedChildrenTypes>[];
  /**
   * invoked for controlled components in the event the accordion
   * is requesting to open or close
   */
  onToggle?: (newValue: boolean) => void;

  accessibilityLabel?: string;
};

export const Accordion: FC<AccordionProps> = ({
  children,
  disabled = false,
  expanded,
  defaultExpanded = false,
  hideBorder = false,
  onToggle,
  accessibilityLabel,
}) => {
  const [summary, ...restChildren] = Children.toArray(children);

  // if not in production mode log error for invalid first child
  if (process.env.NODE_ENV !== "production") {
    // The environment is fixed after the compile time so it's okay.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if ((summary as ReactElement).type !== AccordionSummary) {
        console.error("First child provided to Accordion was not a AccordionSummary");
      }
      // We just want to display this warning one time only for each instance of this component.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  }

  const [isExpanded, setIsExpanded] = useControlled({
    controlled: expanded,
    default: defaultExpanded,
    componentName: "Accordion",
    stateName: "expanded",
  });

  const toggleExpandedState = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (onToggle) {
      onToggle(!isExpanded);
    }
  }, [isExpanded, onToggle, setIsExpanded]);

  const accordionContextState: AccordionContextState = useMemo(
    () => ({
      disabled,
      expanded: Boolean(isExpanded),
      toggle: toggleExpandedState,
    }),
    [isExpanded, disabled, toggleExpandedState]
  );

  return (
    // TODO: need to think about making the accessible. will come back to
    <View
      style={[
        tailwind("overflow-hidden"),
        tailwind(hideBorder ? "" : "border-b border-gray-100_50 border-solid"),
      ]}
    >
      <AccordionContext.Provider value={accordionContextState}>
        <View>{summary}</View>
      </AccordionContext.Provider>

      <Collapsible
        animationSpeed={ANIMATION_OPEN_CLOSE_DURATION}
        isCollapsed={!accordionContextState.expanded}
        accessibilityLabel={accessibilityLabel}
      >
        {restChildren}
      </Collapsible>
    </View>
  );
};

export default Accordion;
